import React from 'react';

import { Popover } from '@mui/material';

import { COUNTRIES } from 'components/CountrySelect/countries';

import { RuleCombinator, RuleConditionResponse, RuleVersion } from 'utils/api/modules/memberGroups';

import { ATTRIBUTE_LABELS, INLINE_VALUE_LIMIT, isNoValueOperator, OPERATOR_LABELS } from './registry';
import {
  RuleChip,
  RuleChipAttr,
  RuleChipMore,
  RuleChips,
  RuleChipValue,
  RuleJoinWord,
  RuleValueList,
  RuleValueListHeader,
  RuleValueListItem,
} from './styles';

const valueLabel = (attribute: string, value: string | { uuid: string; name: string }): string => {
  if (typeof value !== 'string') {
    return value.name;
  }
  if (attribute === 'country') {
    return COUNTRIES.find((c) => c.code === value)?.name ?? value;
  }
  return value;
};

const attributeLabel = (attribute: string, customFieldLabels: Record<string, string>): string =>
  ATTRIBUTE_LABELS[attribute] ?? customFieldLabels[attribute] ?? attribute;

const ConditionValues: React.FunctionComponent<{ condition: RuleConditionResponse }> = ({ condition }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const labels = condition.values.map((value) => valueLabel(condition.attribute, value));
  const shown = labels.slice(0, INLINE_VALUE_LIMIT);
  const hiddenCount = labels.length - shown.length;

  return (
    <RuleChipValue>
      {shown.join(', ')}
      {hiddenCount > 0 && (
        <>
          <RuleChipMore type="button" onClick={(e) => setAnchorEl(e.currentTarget)}>
            +{hiddenCount}
          </RuleChipMore>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <RuleValueList>
              <RuleValueListHeader>{labels.length} values</RuleValueListHeader>
              {labels.map((label, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <RuleValueListItem key={`${label}-${index}`}>{label}</RuleValueListItem>
              ))}
            </RuleValueList>
          </Popover>
        </>
      )}
    </RuleChipValue>
  );
};

export interface RuleSummaryProps {
  rule: RuleVersion;
  customFieldLabels?: Record<string, string>;
}

const RuleSummary: React.FunctionComponent<RuleSummaryProps> = ({ rule, customFieldLabels = {} }) => {
  if (!rule.conditions.length) {
    return null;
  }
  const joinWord = rule.combinator === RuleCombinator.All ? 'AND' : 'OR';

  return (
    <RuleChips>
      {rule.conditions.map((condition, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 && <RuleJoinWord>{joinWord}</RuleJoinWord>}
          <RuleChip>
            <RuleChipAttr>
              {attributeLabel(condition.attribute, customFieldLabels)} {OPERATOR_LABELS[condition.operator]}
            </RuleChipAttr>
            {!isNoValueOperator(condition.operator) && <ConditionValues condition={condition} />}
          </RuleChip>
        </React.Fragment>
      ))}
    </RuleChips>
  );
};

export default RuleSummary;
